import React, { useState, useEffect } from 'react'
import {
  Box,
  TextField,
  Typography,
  FormHelperText,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { alpha } from '@mui/material/styles'
import { ConfigFieldProps } from '../types/EnvironmentConfig.types'

/**
 * ConfigField Component
 * A reusable form field component with validation and help text
 */
const ConfigField: React.FC<ConfigFieldProps> = ({
  label,
  value,
  onChange,
  onBlur,
  onKeyDown,
  type = 'text',
  placeholder,
  required = false,
  disabled = false,
  error,
  helpText,
  validation,
}) => {
  const theme = useTheme()
  const [localError, setLocalError] = useState<string | undefined>(error)
  const [isFocused, setIsFocused] = useState(false)

  const primary = theme.palette.primary.main
  const errorMain = theme.palette.error.main
  const borderDefault = theme.palette.divider

  // Update local error when prop changes
  useEffect(() => {
    setLocalError(error)
  }, [error])

  // Validate on value change
  useEffect(() => {
    if (validation && value !== undefined) {
      const validationError = validation(value)
      setLocalError(validationError)
    }
  }, [value, validation])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
    onChange(newValue)
  }

  const handleFocus = () => {
    setIsFocused(true)
  }

  const handleBlur = () => {
    setIsFocused(false)
    // Validate on blur
    if (validation && value !== undefined) {
      const validationError = validation(value)
      setLocalError(validationError)
    }
  }

  const getInputProps = () => {
    const baseProps: any = {
      'aria-describedby': helpText ? `${label}-help-text` : undefined,
    }

    // Add type-specific props
    switch (type) {
      case 'email':
        baseProps.autoComplete = 'email'
        break
      case 'password':
        baseProps.autoComplete = 'current-password'
        break
      case 'tel':
        baseProps.autoComplete = 'tel'
        break
      case 'url':
        baseProps.autoComplete = 'url'
        break
    }

    return baseProps
  }

  const outlineColor = localError ? errorMain : isFocused ? primary : borderDefault

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
          }}
        >
          {label}
          {required && (
            <Typography
              component="span"
              sx={{
                color: 'error.main',
                ml: 0.5,
              }}
            >
              *
            </Typography>
          )}
        </Typography>

        {helpText && (
          <Tooltip
            title={helpText}
            placement="top"
            arrow
            sx={{
              maxWidth: 300,
            }}
          >
            <IconButton
              size="small"
              sx={{
                ml: 1,
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                  bgcolor: alpha(primary, 0.08),
                },
              }}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <TextField
        type={type}
        value={value || ''}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={onBlur || handleBlur}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        error={!!localError}
        fullWidth
        size="small"
        variant="outlined"
        inputProps={getInputProps()}
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            color: 'text.primary',
            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
            transition: theme.transitions.create(['box-shadow', 'background-color'], {
              duration: theme.transitions.duration.shortest,
            }),
            '&:hover': {
              bgcolor: 'background.paper',
            },
            '&.Mui-focused': {
              bgcolor: 'background.paper',
              boxShadow: `0 0 0 2px ${alpha(primary, 0.28)}`,
            },
            '&.Mui-error': {
              boxShadow: `0 0 0 2px ${alpha(errorMain, 0.28)}`,
            },
            '&.Mui-disabled': {
              bgcolor: 'action.hover',
              color: 'text.disabled',
            },
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: outlineColor,
            transition: theme.transitions.create('border-color', {
              duration: theme.transitions.duration.shortest,
            }),
          },
          '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: localError ? errorMain : primary,
          },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: primary,
            borderWidth: 2,
          },
          '& .MuiInputLabel-root': {
            color: 'text.secondary',
            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
            '&.Mui-focused': {
              color: 'primary.main',
            },
            '&.Mui-disabled': {
              color: 'text.disabled',
            },
          },
          '& .MuiInputBase-input': {
            color: 'text.primary',
            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
            '&::placeholder': {
              color: 'text.disabled',
              opacity: 1,
            },
          },
        }}
      />

      {/* Error Message */}
      {localError && (
        <FormHelperText
          error
          sx={{
            mt: 0.5,
            fontSize: '0.75rem',
            fontWeight: 500,
            color: 'error.main',
            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
          }}
        >
          {localError}
        </FormHelperText>
      )}

      {/* Help Text */}
      {helpText && !localError && (
        <FormHelperText
          id={`${label}-help-text`}
          sx={{
            mt: 0.5,
            fontSize: '0.75rem',
            color: 'text.secondary',
            lineHeight: 1.6,
            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
          }}
        >
          {helpText}
        </FormHelperText>
      )}
    </Box>
  )
}

export default ConfigField
